import { LitElement, PropertyValues, css, html } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import { ref, createRef, Ref } from "lit/directives/ref.js";

import { getBPMNDimensions } from "@/composables/bpmn-manager/helpers";
import type ElementRegistry from "diagram-js/lib/core/ElementRegistry";
import BaseRenderer from "diagram-js/lib/draw/BaseRenderer";
import {
    remove as svgRemove,
    attr as svgAttr,
    create as svgCreate,
} from "tiny-svg";
import { debounce } from "lodash";
import { api } from "@/lib/openapi";
import { headerManager } from "@/lib/requests";
import * as userArtefacts from "@/lib/artefacts/user";
import type { ArtefactMeta, ArtefactGroupWithArtefacts } from "@/lib/artefacts";

class NotFoundError extends Error {}

function isNotArtefactMetaGroup(
    artefact: ArtefactMeta | ArtefactGroupWithArtefacts
): artefact is ArtefactMeta {
    return Boolean("artefacts" in artefact);
}

const bpmnWidgetConfig = {
    // matches the bpmn-js's HIGH_PRIORITY value
    renderingPriority: 1500,
    // The duration in which the transition animation should be completed.
    transitionAnimationDuration: 2000,
    // The time in which multiple transitions should complete. If there
    // are 3 transitions, each of which take 2000 ms to complete, the
    // first one should start at 333ms, the second one at 666ms, and the
    // third one at 999ms.
    showTransitionsWithinMilliseconds: 3000,
    updateFakeValueInterval: 200,
    updateDisplayedValuesInterval: 200,
    overlayContainerWidth: 100,
};

// The bpmn widget takes a bpmn diagram and renders it in a specific way using
// a custom renderer based on the widget configuration. It also enriches the
// bpmn diagram with state information, and visualizes transitions taking
// place within that bpmn diagram. It is meant to be used with the spex engine,
// but it can also be used with fake values to showcase functionalities.
@customElement("c-bpmn-widget")
export class BpmnWidgetComponent extends LitElement {
    static styles = css`
        #bpmn-container,
        :host {
            display: block;
            height: 100%;
        }

        .display-value {
            width: ${bpmnWidgetConfig.overlayContainerWidth}px;
            text-align: right;
        }

        span {
            position: absolute;
            text-align: center;
        }

        span.title {
            width: 100%;
        }

        span.toggle {
            right: 0;
            z-index: 1000;
        }
    `;

    fakeValueGeneratorIntervalId: number;
    updateDisplayIntervalId: number;

    @state()
    showPercentage = false;
    bpmnContainerRef: Ref<HTMLDivElement> = createRef();
    bpmnViewer: Viewer;

    // Maps the bpmn diagram elements that we display in our widget to
    // the processIds used by spex
    processAndBpmnElementIdsToSpexElementIds: {
        // The processId in spex
        [processId: string]: {
            // The elementId in our bpmn
            [elementId: string]: {
                // Note, these are the bpmn element ids as they are known to
                // the spex engine, which could be different from the id for
                // the element in the bpmn diagram shown in this widget.
                elementIdsKnownToSpex: string[];
            };
        };
    } = {};

    // The values that are extracted from spex. These can't be directly mapped
    // to the bpmn diagram, because in some cases the values are combined
    // through a formula.
    spexValues: {
        [processIdAndEntityId: string]: number;
    } = {};

    // By keeping track of the values after the last state transition, we can
    // derive the state transitions by comparing these with the current values.
    valuesInBpmnAfterLastStateTransition: {
        [elementId: string]: number;
    } = {};

    // Contains an approximation to visualize state stateTransitions between
    // processes.
    stateTransitionsInBpmn: {
        // An element can have multiple sources
        [elementId: string]: {
            // For each of those sources we might have to show increments
            [sequenceFlowId: string]: number;
        };
    } = {};

    // The values that are calculated or extracted from spex for each element
    // in the diagram.
    valuesInBpmn: {
        [elementId: string]: number;
    } = {};

    // The values that are displayed for each element in the diagram. By
    // keeping track of this we can prevent re-firing associated effects (e.g.
    // transitions) when the value hasn't changed.
    pendingValuesInBpmnAfterStateTransitions: {
        [elementId: string]: number;
    } = {};

    viewConfigs = {
        "default": {
            hideLanes: false,
            hideTaskBorders: false,
            colorSequenceFlows: false,
        },
        "simplified": {
            hideLanes: true,
            hideTaskBorders: true,
            colorSequenceFlows: false,
        },
    };

    @property({ attribute: "data-view" })
    view: "default" | "simplified" = "simplified";

    @property({ attribute: "data-process-id" })
    processId: string;

    @property({ attribute: "data-hide-lanes", type: Boolean })
    hideLanes: boolean = false;

    @property({ attribute: "data-hide-state-transfers", type: Boolean })
    hideStateTransfers: boolean = true;

    @property({ attribute: "data-use-fake-values", type: Boolean })
    useFakeValues: boolean = true;

    willUpdate(changedProperties: PropertyValues<this>) {
        if (changedProperties.has("showPercentage")) {
            this.updateAllValues();
        }
    }

    updateAllValues() {
        // Could be that we're temporarily disconnected, e.g. when configuring
        // the widget.
        if (!this.bpmnContainerRef.value) {
            return;
        }

        let total = 0;
        if (this.showPercentage) {
            total = Object.values(this.valuesInBpmn)
                .filter((value) => !isNaN(value))
                .reduce((acc, value) => acc + value, 0);
        }

        this.bpmnContainerRef.value
            .querySelectorAll(".display-value")
            .forEach((displayValueNode) => {
                const entityId = displayValueNode.dataset.entityId;
                const value = this.valuesInBpmn[entityId];
                this.displayValue(displayValueNode, value, total);
            });
    }

    displayValue(node: HTMLElement, value: number, total: number) {
        if (this.showPercentage) {
            node.textContent = `${Math.round((value / total) * 100)}%`;
        } else {
            node.textContent = `${value} (total)`;
        }
    }

    // Creating a class scoped to this widget instance, to be able to use widget
    // instance variables in the custom renderer. Haven't yet found a way to
    // pass in variables to the custom renderer in a different way. Might be
    // worth it look into this, so that we can abstract the renderer away from
    // the widget instance.
    getWidgetRenderer() {
        // eslint-disable-next-line @typescript-eslint/no-this-alias
        const widgetInstance = this;

        return class CustomRenderer extends BaseRenderer {
            bpmnRenderer: BpmnRenderer;

            constructor(eventBus, bpmnRenderer) {
                super(eventBus, bpmnWidgetConfig.renderingPriority);
                this.bpmnRenderer = bpmnRenderer;
            }

            canRender(element) {
                return [
                    "bpmn:Task",
                    //"bpmn:UserTask",
                    "bpmn:Event",
                    "bpmn:Participant",
                    "bpmn:Lane",
                    "bpmn:SequenceFlow",
                ].includes(element.type);
            }

            // Draws the shape around an element. Allows us to display
            // elements in a non-default way.
            drawShape(parentNode, element) {
                const viewConfig =
                    widgetInstance.viewConfigs[widgetInstance.view];
                if (
                    element.type === "bpmn:Participant" &&
                    viewConfig.hideLanes
                ) {
                    return "";
                }

                if (element.type === "bpmn:Lane" && viewConfig.hideLanes) {
                    return "";
                }

                const shape = this.bpmnRenderer.drawShape(parentNode, element);

                if (
                    ["bpmn:Task", "bpmn:UserTask"].includes(element.type) &&
                    viewConfig.hideTaskBorders
                ) {
                    svgRemove(shape);
                    return shape;
                }

                return shape;
            }

            // Responsible for drawing the connection between two elements.
            drawConnection(parentNode, element) {
                const viewConfig =
                    widgetInstance.viewConfigs[widgetInstance.view];
                const connection = this.bpmnRenderer.drawConnection(
                    parentNode,
                    element
                );

                if (!viewConfig.colorSequenceFlows) {
                    return connection;
                }

                svgAttr(connection, {
                    fill: "none",
                    // Here you choose the new color, but not doing so
                    // because that would give a mismatch between the
                    // color of the marker-end and the color of the
                    // line itself.
                    //stroke: '#4a7cd5',
                    strokeWidth: 3,
                    // Setting the 'marker-end' color is quite cumbersome
                    // because you would have to reimplement inaccessible
                    // scoped variables from the bpmnRenderer class.
                    //"marker-end": something
                });

                return connection;
            }
        };
    }

    render() {
        return html`
            <span class="title"><h1>QA Status Overview</h1></span>
            <span class="toggle">Show percentage<input type="checkbox"/ @change=${() => {
                this.showPercentage = !this.showPercentage;
            }}></span>
            <div id="bpmn-container" ${ref(this.bpmnContainerRef)}></div>
        `;
    }

    async getBPMNXml() {
        await headerManager.allHeadersSet;

        const { data, response } =
            await api.startProcessProcessMgtGetProcessStartPost({
                body: { process_id: this.processId },
            });
        if (!response.ok || !data) {
            return;
        }

        const artefacts = await userArtefacts.getArtefactsTree(
            data.user_artefact_group_id || ""
        );
        // TODO: Introduce logic to deal with folders // nested artefact groups. - FE#373
        const bpmnArtefacts: ArtefactMeta[] = artefacts
            .filter(isNotArtefactMetaGroup)
            .filter((artefact) => artefact.mimetype === "application/bpmn+xml");

        if (bpmnArtefacts.length === 0) {
            throw new Error("No BPMN artefacts found");
        }

        const firstBpmnArtefact = bpmnArtefacts[0];

        const artefact = await userArtefacts.get(firstBpmnArtefact.identity);
        return artefact.content;
    }

    // A way to keep the size of the bpmn diagram tied to the size of the
    // widget in the dashboard.
    setupResizeObserver() {
        const resize = () => {
            if (!this.isConnected) {
                return;
            }

            const dimensions = getBPMNDimensions(this.bpmnViewer);
            const canvas = this.bpmnViewer.get("canvas");
            // When toggling between edit and view mode, the canvas
            // might not exist anymore.
            if (!this.clientWidth || !this.clientHeight) {
                return;
            }

            canvas.viewbox({
                x: dimensions.centerX - this.clientWidth / 2,
                y: dimensions.centerY - this.clientHeight / 2,
                width: this.clientWidth,
                height: this.clientHeight,
            });

            const width = this.bpmnContainerRef.value.offsetWidth;
            canvas.zoom(width / dimensions.width);
        };
        // Immediately call it to make it appear like the bpmn diagram is sized correctly.
        resize();

        const debounceResize = debounce(resize, 100);
        const resizeObserver = new ResizeObserver(() => {
            debounceResize();
        });
        resizeObserver.observe(this.bpmnContainerRef.value);
    }

    // Creates a mapping from spex process ids and their associated element ids
    // to the bpmn elements ids that we use in the bpmn diagram.
    //
    // By using this map, we can easily determine what states are to be queried
    // from spex and what states should be represented at what position in the
    // bpmn diagram.
    //
    // It can deal with bpmns directly, but it is also able to deal with 'widget'
    // bpmns, which contain bpmn extensions for spex, where those extensions
    // describe what states should be represented, and where it would get the
    // values for those states.
    setupSpexMapping() {
        const elementRegistry = this.bpmnViewer.get(
            "elementRegistry"
        ) as ElementRegistry;

        const elementTypes = [
            "bpmn:StartEvent",
            "bpmn:Task",
            "bpmn:UserTask",
            "bpmn:ExclusiveGateway",
            "bpmn:IntermediateThrowEvent",
            "bpmn:sendTask",
            "bpmn:EndEvent",
        ];

        const elements = elementTypes.flatMap((type) =>
            elementRegistry.filter((el) => el.type === type)
        );

        const rootElement = this.bpmnViewer.get("canvas").getRootElement();
        let processId = rootElement.businessObject.id;
        if (rootElement.businessObject.$type === "bpmn:Collaboration") {
            // Assuming the process is the same across participants, if any.
            processId =
                rootElement.businessObject.participants[0].processRef.id;
        }
        elements.forEach((element) => {
            this.processAndBpmnElementIdsToSpexElementIds[processId] ??= {};

            // By default, we use the element id as a 1-1 mapping to the
            // identity used to represent an element in spex.
            this.processAndBpmnElementIdsToSpexElementIds[processId][
                element.id
            ] = {
                elementIdsKnownToSpex: [element.id],
            };
        });
    }

    setupFakeValueGenerator() {
        // Just call this once to initialize the element map so we can start
        // generating fake values. In case spex is enabled this mapping will
        // be a bit more sophisticated.
        this.updateCalculatedValues();

        // First set all values to 0.
        Object.keys(this.valuesInBpmn).forEach((key) => {
            this.valuesInBpmn[key] = 0;
        });

        const elementRegistry = this.bpmnViewer.get(
            "elementRegistry"
        ) as ElementRegistry;

        const elementIdsWithoutIncomingOrOutgoing = elementRegistry.filter(
            (element) => {
                return (
                    element.incoming.length === 0 &&
                    element.outgoing.length === 0
                );
            }
        );

        // Make sure there is always at least 1 starting element with a value.
        const elementIds = Object.keys(this.valuesInBpmn);
        this.valuesInBpmn[elementIds[0]] += 1;

        this.fakeValueGeneratorIntervalId = window.setInterval(() => {
            // Randomly select one of the tasks
            const elementIds = Object.keys(this.valuesInBpmn);
            const elementIdsWithOutgoing = elementIds.filter(
                (elementId) =>
                    elementRegistry.get(elementId).outgoing.length > 0
            );
            const elementIdsForElementsWithValues =
                elementIdsWithOutgoing.filter(
                    (elementId) => this.valuesInBpmn[elementId] > 0
                );

            // 20% chance a new value is added to the start
            if (
                (Math.random() < 0.2 ||
                    elementIdsForElementsWithValues.length === 0) &&
                this.valuesInBpmn[elementIds[0]] < 10
            ) {
                this.valuesInBpmn[elementIds[0]] += 1;
                // 80% chance something happens to an existing value
            } else {
                const elementId =
                    elementIdsForElementsWithValues[
                        Math.floor(
                            Math.random() *
                                elementIdsForElementsWithValues.length
                        )
                    ];
                const element = elementRegistry.get(elementId);
                if (element.outgoing.length === 0) {
                    return;
                }

                const rand = Math.random();
                // 20% chance (of that 80%) to select the first outgoing connection
                if (rand < 0.2) {
                    const newId =
                        element.outgoing[0].businessObject.targetRef.id;
                    if (!(newId in this.valuesInBpmn)) {
                        console.log(
                            `Element ${element.outgoing[0].businessObject.targetRef.$type}:${newId} not in valuesInBpmn`
                        );
                        return;
                    }

                    this.valuesInBpmn[newId] += 1;
                    this.valuesInBpmn[elementId] -= 1;
                    // 10% (of that 80%) chance to select a random outgoing connection
                } else if (rand < 0.3) {
                    const randomIndex = Math.max(
                        Math.floor(Math.random() * element.outgoing.length),
                        element.outgoing.length - 1
                    );
                    const newId =
                        element.outgoing[randomIndex].businessObject.targetRef
                            .id;

                    if (!(newId in this.valuesInBpmn)) {
                        console.log(
                            `Element ${element.outgoing[randomIndex].businessObject.targetRef.$type}:${newId} not in valuesInBpmn `
                        );
                        return;
                    }
                    this.valuesInBpmn[newId] += 1;
                    this.valuesInBpmn[elementId] -= 1;
                }
                // 70% (of that 80%) chance nothing happens!
            }

            // 15% chance a element without an incoming or outgoing connection
            // is increased in value
            if (Math.random() < 0.15) {
                const element =
                    elementIdsWithoutIncomingOrOutgoing[
                        Math.floor(
                            Math.random() *
                                elementIdsWithoutIncomingOrOutgoing.length
                        )
                    ];
                this.valuesInBpmn[element.id] += 1;
            }
        }, bpmnWidgetConfig.updateFakeValueInterval);
        setTimeout(() => {
            this.updateDisplayIntervalId = window.setInterval(() => {
                if (!this.hideStateTransfers) {
                    this.calculateTransfers();
                }
                this.updateDisplayedValues();
            }, bpmnWidgetConfig.updateDisplayedValuesInterval);
        }, 200);
    }

    getOverlayPositionForElement(element) {
        if (
            [
                "bpmn:StartEvent",
                "bpmn:EndEvent",
                "bpmn:IntermediateThrowEvent",
            ].includes(element.type)
        ) {
            return {
                top: -21,
                left: -15,
            };
        }

        const position =
            this.view === "simplified"
                ? {
                      bottom: 20,
                      right: bpmnWidgetConfig.overlayContainerWidth,
                  }
                : {
                      bottom: 0,
                      right: bpmnWidgetConfig.overlayContainerWidth,
                  };
        return position;
    }

    // Overlays allow you to create nodes for elements in the diagram. These
    // nodes are used to simplify displaying values that associated with these
    // elements.
    setupOverlays() {
        const overlays = this.bpmnViewer.get("overlays");
        const entityIds = Object.values(
            this.processAndBpmnElementIdsToSpexElementIds
        ).flatMap((entityIdsToSpexElementIds) =>
            Object.keys(entityIdsToSpexElementIds)
        );

        const elementRegistry = this.bpmnViewer.get(
            "elementRegistry"
        ) as ElementRegistry;

        entityIds.forEach((entityId) => {
            const element = elementRegistry.get(entityId);

            overlays.add(entityId, {
                position: this.getOverlayPositionForElement(element),
                html: `<div class="display-value" data-entity-id=${entityId}></div>`,
            });
        });
    }

    updateCalculatedValues() {
        Object.entries(this.processAndBpmnElementIdsToSpexElementIds).forEach(
            ([processId, values]) => {
                Object.entries(values).forEach(
                    ([elementId, spexReflection]) => {
                        const values = spexReflection.elementIdsKnownToSpex.map(
                            (id) => this.spexValues[`${processId}:${id}`]
                        );

                        // If there's no formula, there should only be
                        // one value.
                        this.valuesInBpmn[elementId] = values[0];
                    }
                );
            }
        );
    }

    elementTargets: {
        [elementId: string]: string[];
    } = {};
    getTargetsOfElement(elementId: string, originalElementId?: string) {
        if (!originalElementId) {
            originalElementId = elementId;
        }

        // If the calculation happened before, the targets are already determined,
        // and the array is frozen, so we can just return the old list of
        // targets instead.
        if (
            originalElementId in this.elementTargets &&
            Object.isFrozen(this.elementTargets[originalElementId])
        ) {
            return this.elementTargets[originalElementId];
        }

        const elementRegistry = this.bpmnViewer.get(
            "elementRegistry"
        ) as ElementRegistry;

        const element = elementRegistry.get(elementId);
        if (element.outgoing.length === 0) {
            return [];
        }

        const targets = element.outgoing
            .filter((outgoing) => outgoing.type === "bpmn:SequenceFlow")
            .map((outgoing) => outgoing.businessObject.targetRef);

        if (!(originalElementId in this.elementTargets)) {
            this.elementTargets[originalElementId] = [];
        }

        targets.forEach((target) => {
            // If this happens, the (possibly nested) target of the original
            // element is the element itself. Basically, a loop is detected.
            // At this point we don't need to get the targets of the original
            // element again.
            if (target.id === originalElementId) {
                return;
            }

            // Another situation could be that a target of a target has itself
            // as a target. To prevent an infinite loop, jump out of it, while
            // leaving 2 targets in the list to leave a hint of a duplicate
            // target, which can later be detected by the 'getSharedTargetsOfOutgoing'
            // function.
            if (
                this.elementTargets[originalElementId].filter(
                    (item) => item === target.id
                ).length > 2
            ) {
                return;
            }

            this.elementTargets[originalElementId].push(target.id);

            this.getTargetsOfElement(target.id, originalElementId);
        });

        // After doing these calculations, we can freeze the array, to prevent
        // future modifications. This isn't just used to prevent the array
        // from being modified, but it is also used to detect whether the
        // calculation has already been done before.
        if (elementId === originalElementId) {
            Object.freeze(this.elementTargets[originalElementId]);
        }

        return this.elementTargets[originalElementId];
    }

    elementSources: {
        [elementId: string]: string[];
    } = {};
    getSourceIdsOfElementId(elementId: string, originalElementId?: string) {
        if (!originalElementId) {
            originalElementId = elementId;
        }

        // If the calculation happened before, the sources are already determined
        // and the array is frozen, so we can just return the old list of
        // sources instead.
        if (
            originalElementId in this.elementSources &&
            Object.isFrozen(this.elementSources[originalElementId])
        ) {
            return this.elementSources[originalElementId];
        }

        const elementRegistry = this.bpmnViewer.get(
            "elementRegistry"
        ) as ElementRegistry;

        const element = elementRegistry.get(elementId);
        if (element.incoming.length === 0) {
            return [];
        }

        const sources = element.incoming
            .filter((incoming) => incoming.type === "bpmn:SequenceFlow")
            .map((incoming) => incoming.businessObject.sourceRef);

        if (!(originalElementId in this.elementSources)) {
            this.elementSources[originalElementId] = [];
        }

        sources.forEach((source) => {
            // If this happens, the (possibly nested) source of the original
            // element is the element itself. Basically, a loop is detected.
            // At this point we don't need to get the sources of the original
            // element again.
            if (source.id === originalElementId) {
                return;
            }

            // Another situation could be that a source of a source has itself
            // as a source. To prevent an infinite loop, jump out of it, while
            // leaving 2 sources in the list to leave a hint of a duplicate
            // source, which can later be detected by the 'getSharedSourcesOfIncoming'
            // function.
            if (
                this.elementSources[originalElementId].filter(
                    (item) => item === source.id
                ).length > 2
            ) {
                return;
            }

            this.elementSources[originalElementId].push(source.id);

            this.getSourceIdsOfElementId(source.id, originalElementId);
        });

        // After doing these calculations, we can freeze the array, to prevent
        // future modifications. This isn't just used to prevent the array
        // from being modified, but it is also used to detect whether the
        // calculation has already been done before.
        if (elementId === originalElementId) {
            Object.freeze(this.elementSources[originalElementId]);
        }

        return this.elementSources[originalElementId];
    }

    // Get shared sources of all incoming sources for a given element. This
    // includes sources of sources.
    getSharedSourcesOfIncoming(elementId: string) {
        const sourceIds = this.getSourceIdsOfElementId(elementId);
        return sourceIds.filter(
            (item, index) => index !== sourceIds.indexOf(item)
        );
    }
    // Get shared targets of all outgoing targets for a given element. This
    // includes targets of targets.
    getSharedTargetsOfOutgoing(elementId: string) {
        const targetIds = this.getTargetsOfElement(elementId);
        return targetIds.filter(
            (item, index) => index !== targetIds.indexOf(item)
        );
    }

    // For a given element, grab the derivatives for a group of targets of
    // that element, combine them and turn them into a single increment.
    //
    // Note that this function expects you to pass element ids to be excluded
    // from the calculation, to prevent infinite loops in this calculation,
    // while allowing you to get an 'as-large-as-possible' grouping of element
    // targets for a given element and get the combined increment for that
    // group.
    getCombinedIncrementForElementTargets(
        elementId: string,
        excludeElementIds: string[]
    ) {
        // Build a list of sources of sources for element
        const elementRegistry = this.bpmnViewer.get(
            "elementRegistry"
        ) as ElementRegistry;

        let value = 0;
        if (excludeElementIds.includes(elementId)) {
            return value;
        }

        const element = elementRegistry.get(elementId);

        // If there are no incoming connections, then there is no increment
        // to account for.
        if (element.incoming.length === 0) {
            return value;
        }

        return element.outgoing
            .filter(
                (outgoing) =>
                    !excludeElementIds.includes(
                        outgoing?.businessObject?.targetRef?.id
                    )
            )
            .reduce((acc, outgoing) => {
                if (outgoing.type !== "bpmn:SequenceFlow") {
                    return acc;
                }

                const targetRef = outgoing.businessObject.targetRef;
                if (!(targetRef.id in this.valuesInBpmn)) {
                    return acc;
                }

                const value =
                    this.valuesInBpmn[targetRef.id] -
                    this.valuesInBpmnAfterLastStateTransition[targetRef.id] +
                    this.getCombinedIncrementForElementTargets(
                        targetRef.id,
                        excludeElementIds
                    );

                return acc + value;
            }, 0);
    }

    // For a given element, grab the derivatives for a group of sources of
    // that element, combine them and turn them into a single decrement.
    //
    // Note that this function expects you to pass element ids to be excluded
    // from the calculation, to prevent infinite loops in this calculation,
    // while allowing you to get an 'as-large-as-possible' grouping of element
    // sources for a given element and get the combined decrement for that
    // group.
    getCombinedDecrementForElementSources(
        elementId: string,
        excludeElementIds: string[]
    ) {
        // Build a list of sources of sources for element
        const elementRegistry = this.bpmnViewer.get(
            "elementRegistry"
        ) as ElementRegistry;

        const element = elementRegistry.get(elementId);
        let value = 0;

        // If there are no incoming connections, then there is no increment
        // to account for.
        if (element.incoming.length === 0) {
            return value;
        }

        return element.incoming
            .filter(
                (incoming) =>
                    !excludeElementIds.includes(
                        incoming?.businessObject?.sourceRef?.id
                    )
            )
            .reduce((acc, incoming) => {
                if (incoming.type !== "bpmn:SequenceFlow") {
                    return acc;
                }

                const sourceRef = incoming.businessObject.sourceRef;
                if (!(sourceRef.id in this.valuesInBpmn)) {
                    return acc;
                }

                const value =
                    this.valuesInBpmn[sourceRef.id] -
                    this.valuesInBpmnAfterLastStateTransition[sourceRef.id] +
                    this.getCombinedDecrementForElementSources(
                        sourceRef.id,
                        excludeElementIds
                    );

                return acc + value;
            }, 0);
    }

    // By comparing the previous values with the current values, and making
    // some logical conclusions based on how the connections are laid out,
    // we can deduce where transitions have likely happened within the bpmn
    // diagram. These transitions are stored in the 'stateTransitionsInBpmn'
    // queue, so that they can be processed later on.
    //
    // Note that this is still an approximation, which doesn't yet cover all
    // cases.
    calculateTransfers() {
        const elementRegistry = this.bpmnViewer.get(
            "elementRegistry"
        ) as ElementRegistry;

        Object.entries(this.valuesInBpmn).forEach(([elementId, value]) => {
            // Nothing to do if there are no previous values.
            if (
                Object.keys(this.valuesInBpmnAfterLastStateTransition)
                    .length === 0
            ) {
                return;
            }

            // If there is no increment compared to last time, don't do
            // anything.
            if (this.valuesInBpmnAfterLastStateTransition[elementId] >= value) {
                return;
            }

            // If the value changed from 2 to 4, then we'll try to match
            // the increment of 2 to the incoming connections.
            const valuesToAccountFor =
                value - this.valuesInBpmnAfterLastStateTransition[elementId];

            // Check if the element has incoming connections
            const element = elementRegistry.get(elementId);
            const incoming = element.incoming.filter(
                (element) => element.type === "bpmn:SequenceFlow"
            );
            if (incoming.length === 0) {
                return;
            }

            if (!(elementId in this.stateTransitionsInBpmn)) {
                this.stateTransitionsInBpmn[elementId] = {};
            }

            // If there is only one source, then assume the value came from the
            // source.
            if (incoming.length === 1) {
                this.stateTransitionsInBpmn[elementId][incoming[0].id] =
                    valuesToAccountFor;
                return;
            }

            let accountedForValues = 0;

            const sharedSourcesOfIncoming =
                this.getSharedSourcesOfIncoming(elementId);
            const sharedTargetsOfOutgoing =
                this.getSharedTargetsOfOutgoing(elementId);

            // One of the sources (and sources of sources) might have decremented
            // in value. Figure out which one it is, and likely we'll
            // have the source of our incoming datastream
            incoming.some((incomingElement) => {
                const sourceRef = incomingElement?.businessObject.sourceRef;
                const sumIncreaseInSourcesTargets =
                    this.getCombinedIncrementForElementTargets(sourceRef.id, [
                        elementId,
                        ...sharedTargetsOfOutgoing,
                    ]);
                let sumDecreaseInSources =
                    this.getCombinedDecrementForElementSources(sourceRef.id, [
                        elementId,
                        ...sharedSourcesOfIncoming,
                    ]);

                // From the perspective of F, when the value for F increases
                // while the value for C stays the same, a value could have
                // flowed from B to C in the meantime. When this happens, we
                // can try to deduct this by calculating the net value change
                // of the sources of C, which we do through 'sumDecreaseInSources'.
                //
                // At the same time, the value of C can be reduced because
                // values flow from C to D and its targets. In a similar way
                // we can deduce the net value change of the targets of C,
                // which is done through 'sumIncreaseInSourcesTargets'.
                //
                //           .> F
                //          /
                // A > B > C > D > E
                //
                // Using the example above, the below delta is calculated for
                // C (from the perspective of F, as C is the source of F).
                const decreaseInSourceValue =
                    this.valuesInBpmnAfterLastStateTransition[sourceRef.id] -
                    this.valuesInBpmn[sourceRef.id];
                if (decreaseInSourceValue > 0) {
                    sumDecreaseInSources += decreaseInSourceValue;
                }

                sumDecreaseInSources -= sumIncreaseInSourcesTargets;

                if (sumDecreaseInSources > 0) {
                    // If we deduced that a state transitioned from this source,
                    // store the id of that incoming element and the amount of
                    // state transitions that came from this source.
                    this.stateTransitionsInBpmn[elementId][incomingElement.id] =
                        sumDecreaseInSources;
                    accountedForValues += sumDecreaseInSources;
                }

                if (accountedForValues >= valuesToAccountFor) {
                    return true;
                }
            });
        });

        this.valuesInBpmnAfterLastStateTransition = { ...this.valuesInBpmn };
    }

    hasPendingStateTransitions(entityId: string) {
        if (entityId in this.stateTransitionsInBpmn) {
            return Object.values(this.stateTransitionsInBpmn[entityId]).some(
                (value) => value > 0
            );
        }

        return false;
    }

    // For a given arrow node, show a transitionary effect to indicate that
    // a state transition has happened.
    showStateTransitionForArrowNode(arrowNode: Element) {
        const rect = arrowNode.querySelector("rect");
        const circle = svgCreate("circle");
        // Place the circle exactly on the rectangle that represents the arrow.
        circle.setAttribute("x", rect.getAttribute("x"));
        circle.setAttribute("y", rect.getAttribute("y"));

        const svgNS = "http://www.w3.org/2000/svg";
        const animateMotion = document.createElementNS(svgNS, "animateMotion");
        const path = arrowNode.querySelector("g > path").getAttribute("d");
        animateMotion.setAttribute("path", path);
        animateMotion.setAttribute("dur", "2s");

        circle.appendChild(animateMotion);

        svgAttr(circle, {
            r: 5,
            fill: "rgba(132, 44, 255, 0.7)",
        });
        arrowNode.appendChild(circle);
        // Create a short-lived animation to make it seem like the circle
        // is moving along the path.
        animateMotion.beginElement();

        setTimeout(() => {
            circle.remove();
        }, bpmnWidgetConfig.transitionAnimationDuration);
    }

    // For a given entity id, calculate the timings and trigger the required
    // state transitions visualizations and update the value that should be
    // displayed after the transition.
    visualizeStateTransitionAndUpdateValue(
        entityId: string,
        displayValueNode: HTMLElement,
        endValue: number,
        totalValues: number
    ) {
        Object.entries(this.stateTransitionsInBpmn[entityId]).forEach(
            ([sequenceFlowId, valuesToAccountFor]) => {
                if (!this.bpmnContainerRef.value) {
                    return;
                }

                if (valuesToAccountFor === 0) {
                    return;
                }

                const arrowNode = this.bpmnContainerRef.value.querySelector(
                    `[data-element-id="${sequenceFlowId}"]`
                );
                const timeBetweenIndividualTransitions =
                    (bpmnWidgetConfig.showTransitionsWithinMilliseconds -
                        bpmnWidgetConfig.transitionAnimationDuration) /
                    valuesToAccountFor;

                for (let i = 0; i < valuesToAccountFor; i++) {
                    setTimeout(() => {
                        this.showStateTransitionForArrowNode(arrowNode);
                    }, i * timeBetweenIndividualTransitions);
                    setTimeout(
                        () => {
                            const valueAferTransition =
                                endValue - (valuesToAccountFor - i - 1);
                            this.displayValue(
                                displayValueNode,
                                valueAferTransition,
                                totalValues
                            );
                        },
                        i * timeBetweenIndividualTransitions +
                            bpmnWidgetConfig.transitionAnimationDuration
                    );
                }

                this.stateTransitionsInBpmn[entityId][sequenceFlowId] = 0;
            }
        );
    }

    updateDisplayedValues() {
        // Could be that we're temporarily disconnected, e.g. when configuring
        // the widget.
        if (!this.bpmnContainerRef.value) {
            return;
        }

        let total = 0;
        if (this.showPercentage) {
            total = Object.values(this.valuesInBpmn)
                .filter((value) => !isNaN(value))
                .reduce((acc, value) => acc + value, 0);
        }

        this.bpmnContainerRef.value
            .querySelectorAll(".display-value")
            .forEach((displayValueNode) => {
                const entityId = displayValueNode.dataset.entityId;
                const value = this.valuesInBpmn[entityId];
                if (value === undefined) {
                    return;
                }

                // This prevents the value from being updated when there is
                // currently an effect being displayed which delays displaying
                // the value, to make it seem like the value is only
                // updated after the transition.
                if (
                    this.valuesInBpmn[entityId] ===
                    this.pendingValuesInBpmnAfterStateTransitions[entityId]
                ) {
                    return;
                }
                this.pendingValuesInBpmnAfterStateTransitions[entityId] = value;

                if (this.hideStateTransfers) {
                    this.displayValue(displayValueNode, value, total);
                    return;
                }

                // Check if there are any transitionary effects that we can
                // show.
                if (this.hasPendingStateTransitions(entityId)) {
                    this.visualizeStateTransitionAndUpdateValue(
                        entityId,
                        displayValueNode,
                        value,
                        total
                    );
                } else {
                    // When there are no calculated transitionary effects,
                    // we can at least delay updating the value so any
                    // previously triggered transitionary effects have also
                    // finished for a more realistic feel.
                    setTimeout(() => {
                        this.displayValue(displayValueNode, value, total);
                    }, bpmnWidgetConfig.transitionAnimationDuration);
                }
            });
    }

    async firstUpdated() {
        let xml: string;
        try {
            xml = await this.getBPMNXml();
        } catch (e) {
            if (e instanceof NotFoundError) {
                console.log(
                    `Won't render bpmn for process ${this.processId} as it doesn't exist`
                );
                return;
            }
        }

        const WidgetRenderer = this.getWidgetRenderer();
        // Note that below is not required in dev builds, but it is required
        // in production builds. After the dev build, parameter names from
        // the constructor are extracted, after which their names are matched
        // with an internal pool of dependencies. If they exist, they will be
        // injected automatically. However when you run a production build,
        // the constructor parameter names are minified, causing a mismatch
        // between the name of the available dependencies in the pool.
        //
        // By explicitly stating what services needs to be injected for the
        // constructor like below, the correct dependencies will be matched
        // and injected regardless of the minification.
        WidgetRenderer.$inject = ["eventBus", "bpmnRenderer"];

        this.bpmnViewer = new Viewer({
            container: this.bpmnContainerRef.value,
            additionalModules: [
                {
                    __init__: ["widgetRenderer"],
                    widgetRenderer: ["type", WidgetRenderer],
                },
            ],
        });

        await this.bpmnViewer.importXML(xml);

        this.setupResizeObserver();
        this.setupSpexMapping();
        this.setupOverlays();
        //this.setupFakeValueGenerator();
        this.fakeSomeValues();
    }

    fakeSomeValues() {
        // Just call this once to initialize the element map so we can start
        // generating fake values. In case spex is enabled this mapping will
        // be a bit more sophisticated.
        this.updateCalculatedValues();
        Object.keys(this.valuesInBpmn).forEach((key) => {
            this.valuesInBpmn[key] = Math.floor(Math.random() * 100);
        });
        this.updateDisplayedValues();
    }

    disconnectedCallback() {
        super.disconnectedCallback();
        if (this.fakeValueGeneratorIntervalId) {
            clearInterval(this.fakeValueGeneratorIntervalId);
        }
        if (this.updateDisplayIntervalId) {
            clearInterval(this.updateDisplayIntervalId);
        }
    }
}
